import { useTranslation } from 'gatsby-plugin-react-i18next';
import { graphql, useStaticQuery } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';

import Link from '../Atoms/Link';
import { ImageGatsby } from '../Atoms/Image';

import ArrowButton from '../Atoms/ArrowButton';
import { BUYING_CATALOG_ROUTE } from '../../utils/routes/const';

const imageQuery = graphql`
{
  audemarsPiguet: file(base: {eq: "product_teaser-AudemarsPiguet.jpg"}) {
    childImageSharp {
      gatsbyImageData(
        width: 500
        placeholder: DOMINANT_COLOR
      )
    }
  }
  patekPhilippe: file(base: {eq: "product_teaser-PatekPhilippe.jpg"}) {
    childImageSharp {
      gatsbyImageData(
        width: 500
        placeholder: DOMINANT_COLOR
      )
    }
  }
  rolex: file(base: {eq: "product_teaser-Rolex.jpg"}) {
    childImageSharp {
      gatsbyImageData(
        width: 700
        placeholder: DOMINANT_COLOR
      )
    }
  }
}

`

const ProductsTeaser = ({ paragraphText }: { paragraphText?: string }) => {
  const { t } = useTranslation();

  const { audemarsPiguet, patekPhilippe, rolex } = useStaticQuery(imageQuery);

  return (
    <section className="py-12 px-4 lg:px-8 bg-gray">
      <div className='max-w-screen-2xl w-full mx-auto flex flex-col lg:flex-row'>
        <div className="lg:w-1/12 lg:block hidden" />
        <div className="lg:w-3/12 pr-10">
          <h3 className="text-base lg:text-xl font-cirka mb-5">{t('productsTeaser.subtitle')}</h3>
          <h2 className="text-primary text-3xl md:text-5xl font-cirka mb-10">{t('productsTeaser.title')}</h2>
          {
            paragraphText &&
            <p className="text-lg" dangerouslySetInnerHTML={{ __html: t(paragraphText) }} />
          }
          <Link to={t('productsTeaser.link.url')} className="text-primary font-light font-roboto flex items-center my-8 no-animation">
            <ArrowButton isGhost={false} className="mr-5" />
            {t('productsTeaser.link.text')}
          </Link>
        </div>
        <div className="lg:w-1/12 lg:block hidden" />
        <div className="lg:w-7/12 grid grid-cols-2 gap-5 gap-y-10">
          <div className="h-85 md:h-600 bg-cover relative col-span-2 md:col-span-1 row-span-2 overflow-hidden w-3/4 md:w-full">
            <div className="w-full h-full z-10 bg-black bg-opacity-60 p-8 absolute">
              <h4 className="font-cirka text-primary mb-3 text-4xl md:text-6xl">Rolex</h4>
              <Link to={BUYING_CATALOG_ROUTE('/rolex')} className="no-animation absolute bottom-8 left-8 h-10 w-10">
                <ArrowButton />
              </Link>
            </div>
            <ImageGatsby image={getImage(rolex)} alt="Rolex" className="absolute top-0 -z-10 object-cover md:h-600" width="100%" initialY='25%' />
          </div>
          <div className="h-56 md:h-85 bg-cover relative overflow-hidden">
            <div className="w-full h-full z-10 bg-black bg-opacity-50 p-8 absolute">
              <h4 className="font-cirka text-primary mb-3 text-xl md:text-6xl">Patek Philippe</h4>
              <Link to={BUYING_CATALOG_ROUTE('/patek-philippe')} className="no-animation absolute bottom-8 left-8 h-10 w-10">
                <ArrowButton />
              </Link>
            </div>
            <ImageGatsby image={getImage(patekPhilippe)} alt="Patek Philippe" className="absolute top-0 -z-10 object-cover w-full h-56 md:h-85" />
          </div>
          <div className="h-56 md:h-85 bg-cover relative overflow-hidden">
            <div className="w-full h-full z-10 bg-black bg-opacity-50 p-8 absolute">
              <h4 className="font-cirka text-primary mb-3 text-xl md:text-6xl">Audemars Piguet</h4>
              <Link to={BUYING_CATALOG_ROUTE('/audemars-piguet')} className="no-animation absolute bottom-8 left-8 h-10 w-10">
                <ArrowButton />
              </Link>
            </div>
            <ImageGatsby image={getImage(audemarsPiguet)} alt="Audemars Piguet" className="absolute top-0 -z-10 object-cover w-full h-56 md:h-85" />
          </div>
          <div className="col-span-2 mt-8 md:my-28">
            <h3 className="font-cirka text-lg md:text-xl mb-12">{t('productsTeaser.otherBrands')}</h3>
            <ul className="grid grid-cols-2 md:grid-cols-3 mt-4">
              {brands.map(brand => (
                <li key={brand.name} className="mb-2">
                  <Link to={brand.link} className="text-primary font-light font-roboto text-sm md:text-base">{brand.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductsTeaser;

const brands = [
  {
    name: 'TAG Heuer',
    link: BUYING_CATALOG_ROUTE('/tag-heuer')
  },
  {
    name: 'Rolex',
    link: BUYING_CATALOG_ROUTE('/rolex')
  },
  {
    name: 'A. Lange & Söhne',
    link: BUYING_CATALOG_ROUTE('/a-lange-sohne')
  },
  {
    name: 'Breitling',
    link: BUYING_CATALOG_ROUTE('/breitling')
  },
  {
    name: 'Blancpain',
    link: BUYING_CATALOG_ROUTE('/blancpain')
  },
  {
    name: 'IWC',
    link: BUYING_CATALOG_ROUTE('/iwc')
  },
  {
    name: 'Chopard',
    link: BUYING_CATALOG_ROUTE('/chopard')
  },
  {
    name: 'Omega',
    link: BUYING_CATALOG_ROUTE('/omega')
  },
  {
    name: 'Cartier',
    link: BUYING_CATALOG_ROUTE('/cartier')
  },
  {
    name: 'Audemars Piguet',
    link: BUYING_CATALOG_ROUTE('/audemars-piguet')
  },
  {
    name: 'Panerai',
    link: BUYING_CATALOG_ROUTE('/panerai')
  },
  {
    name: 'Tudor',
    link: BUYING_CATALOG_ROUTE('/tudor')
  },
  {
    name: 'Vacheron Constantin',
    link: BUYING_CATALOG_ROUTE('/vacheron-constantin')
  },
  {
    name: 'Bulgari',
    link: BUYING_CATALOG_ROUTE('/bulgari')
  },
  {
    name: 'Hublot',
    link: BUYING_CATALOG_ROUTE('/hublot')
  },
  {
    name: 'Patek Philippe',
    link: BUYING_CATALOG_ROUTE('/patek-philippe')
  },
  {
    name: 'Jaeger-LeCoultre',
    link: BUYING_CATALOG_ROUTE('/jaeger-lecoultre')
  },
  {
    name: 'Zenith',
    link: BUYING_CATALOG_ROUTE('/zenith')
  },
  {
    name: 'Entre otras…',
    link: BUYING_CATALOG_ROUTE('')
  },
]

